/**
 * utils/previewHelpers.ts
 *
 * Helper utilities for preview mode functionality.
 * Transforms template data into request field format for the dry-run preview.
 *
 * Updated Feb 2026: Replaced condition_catalog system with intra-form
 *   field-based conditional logic (condition_source_field_id + condition_values).
 * Updated Feb 2026: Added logoUrl to PreviewData for the premium UX refactor.
 * Updated Mar 2026: Replaced single condition fields with conditions: FieldCondition[]
 *   array for multi-source OR-logic support.
 * Updated Apr 2026: Added condition_operator to RequestField for AND/OR logic support.
 * Updated Apr 2026: Added is_system to RequestField for system-field identification
 *   (mandatory email question - rendered as email input with format validation).
 */

import type { NeedItem, FieldCondition } from '../types/needs';
import type { FieldType } from '../types/database';

export type { FieldCondition };

export interface PreviewData {
  title: string;
  description: string | null;
  customMessage: string | null;
  logoUrl: string | null;
  fields: RequestField[];
}

export interface RequestField {
  id: string;
  type: FieldType;
  label: string;
  description: string | null;
  is_required: boolean;
  position: number;
  options: string[] | null;
  conditions: FieldCondition[] | null;
  condition_operator: 'or' | 'and';
  section: string | null;
  is_system: boolean;
}

/**
 * Transform NeedItem[] from template data into RequestField[] format.
 */
export function transformNeedsToRequestFields(needs: NeedItem[]): RequestField[] {
  return needs.map((need) => ({
    id: need.id,
    type: need.type,
    label: need.label,
    description: need.description || null,
    is_required: need.is_required,
    position: need.position,
    options: need.options || null,
    conditions: need.conditions || null,
    condition_operator: need.condition_operator || 'or',
    section: need.section || null,
    is_system: need.is_system ?? false,
  }));
}
